/**
 * Service class for managing reading time and related functionalities.
 * This class interacts with various services and modules to control the reading time countdown,
 * keyboard locking, and other related tasks.
 */
class ReadingTimeService {
    /**
     * Constructor for ReadingTimeService.
     * Initializes dependencies and sets up initial state.
     * 
     * @param {Object} $bridges - Service for bridge functionalities.
     * @param {Object} $uibModal - Service for UI modal functionalities.
     * @param {Object} $signals - Service for signal handling.
     * @param {Object} TaskCountdownService - Service for task countdown functionalities.
     * @param {Object} $config - Configuration service.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $location - AngularJS location service.
     * @param {Object} $route - AngularJS route service.
     * @param {Object} $rootScope - AngularJS root scope service.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} IAAPreferencesService - Service for IAA preferences.
     * @param {Object} UserInfoService - Service for user information.
     * @param {Object} InactivityTimeoutService - Service for inactivity timeout.
     * @param {Object} TaskInfoService - Service for task information.
     */
    constructor($bridges, $uibModal, $signals, TaskCountdownService, $config, $timeout, $location, $route, $rootScope, $injector, IAAPreferencesService, UserInfoService, InactivityTimeoutService, TaskInfoService) {
        this.$bridges = $bridges;
        this.$uibModal = $uibModal;
        this.$signals = $signals;
        this.TaskCountdownService = TaskCountdownService;
        this.$config = $config;
        this.$timeout = $timeout;
        this.$location = $location;
        this.$route = $route;
        this.$rootScope = $rootScope;
        this.$injector = $injector;
        this.IAAPreferencesService = IAAPreferencesService;
        this.UserInfoService = UserInfoService;
        this.InactivityTimeoutService = InactivityTimeoutService;
        this.TaskInfoService = TaskInfoService;

        // SMELL: Hardcoded global variable check, should be refactored to use a configuration or service.
        if (typeof isCMS !== 'undefined' && isCMS) {
            return false;
        }

        this._taskInfo = null;
        this._readingTime = 0;

        this.TaskInfoService.getLoadTaskInfoPromise().then(() => {
            this._taskInfo = this.TaskInfoService.getTaskInfo();
            this._readingTime = this._taskInfo.getReadingtimeMillis();
            if (this._taskInfo.isMediaAssessment() || isNaN(this._readingTime)) {
                this._readingTime = 0;
            }
        });

        this._logIdentifier = 'ReadingTimeService';
        this.$logger = this.$bridges.logger();

        this._taskCountdown = this.TaskCountdownService.getInstance();
        this._onExpiredReadingTimeModalOpened = false;
        this._readingTimeStarted = false;
        this._candidateIAAPreferences = this.IAAPreferencesService.getPreferences();
        this._allowedKeyCodes = [33, 34, 37, 38, 39, 40, 98, 99, 100, 102, 104, 105, 113, 114, 81, 85, 73, 84];
    }

    /**
     * Unlocks the keyboard by removing keyup and keydown event listeners.
     */
    unlockKeyboard() {
        $('body').off('keyup keydown');
    }

    /**
     * Locks the keyboard by adding keyup and keydown event listeners.
     * Only allows certain key codes as defined in _allowedKeyCodes.
     */
    lockKeyboard() {
        this.unlockKeyboard();
        if (this._taskCountdown.getMode() === 'readingTime' && this._readingTimeStarted && (this.$route.current.regexp.test('/sheet/:sheetUUID') || this.$route.current.regexp.test('/overview/:taskUUID'))) {
            $('body').on('keyup keydown', (event) => {
                if (this._allowedKeyCodes.indexOf(event.keyCode) === -1) return false;
            });
        }
    }

    /**
     * Disables input in all CKEditor instances by setting them to read-only.
     */
    disableCKEditorInput() {
        if (window.CKEDITOR) {
            angular.forEach(window.CKEDITOR.instances, function (editor) {
                editor.setReadOnly(true);
            });
        }
    }

    /**
     * Enables input in all CKEditor instances by setting them to read-write.
     */
    enableCKEditorInput() {
        if (window.CKEDITOR) {
            angular.forEach(window.CKEDITOR.instances, function (editor, key) {
                editor.setReadOnly(false);
            });
        }
    }

    /**
     * Starts the task by resetting reading time, unlocking the keyboard, and setting the countdown mode to 'task'.
     */
    startTask() {
        console.log("Reading time service: Starting task");
        this._readingTimeStarted = false;
        this.$signals.remove('readingTimeUnlockKeyboard');
        this.$signals.remove('readingTimeLockKeyboard');
        this.unlockKeyboard();
        this._taskCountdown.setMode('task');

        var url = this.$location.path();

        var existingExtraTimeMs = 0,
            alreadyUsedTimeMS = 0,
            candidateUUID = this.$bridges.authentication().getUserUUID(),
            candidate = this.UserInfoService.getUserInfo(candidateUUID);

        var _IAAextraTime = 0;
        if (this._candidateIAAPreferences.extraTime) {
            _IAAextraTime = parseInt(this._candidateIAAPreferences.extraTime) * 60000;
            existingExtraTimeMs = _IAAextraTime;
        }

        var TaskFlowService = this.$injector.get('TaskFlowService'),
            hasExistingData = TaskFlowService.hasExistingDataLastResult();
        if (hasExistingData && (this.$bridges.data().get('timeElapsed') || this.$bridges.data().get("extraTimeTotal"))) {
            alreadyUsedTimeMS = this.$bridges.data().get('timeElapsed');
            if (!angular.isDefined(alreadyUsedTimeMS)) alreadyUsedTimeMS = 0;
            alreadyUsedTimeMS = parseInt(alreadyUsedTimeMS);

            var existingExtraTimeMsStr = this.$bridges.data().get("extraTimeTotal");
            if (!angular.isDefined(existingExtraTimeMsStr) || !existingExtraTimeMsStr) existingExtraTimeMsStr = 0;
            existingExtraTimeMs = parseInt(existingExtraTimeMsStr);
        }

        this._taskCountdown.setTotalExtraTimeMS(existingExtraTimeMs);
        this._taskCountdown.setElapsedMillis(alreadyUsedTimeMS)
        this._taskCountdown.setInitialTime(candidate.getTaskTime());

        if (this._taskCountdown.getIsRunning() === false) {
            var inOverview,
                inSheet;
            if (this.$route.current && this.$route.current.regexp) {
                inOverview = this.$route.current.regexp.test("/overview/:taskUUID");
            }
            if (this.$route.current && this.$route.current.regexp) {
                inSheet = this.$route.current.regexp.test("/sheet/:sheetUUID") || this.$route.current.regexp.test("/sheet/:sheetUUID/:scrollToUUID");
            }

            if (!inOverview && !inSheet) {
                this._taskCountdown.stopMeasure();
                return false;
            }
            this._taskCountdown.startMeasure();
        }

        this.$timeout(() => {            
            this.$location.path(url).search('readingTimeOver', '1');
        }, 1);

        if (angular.isObject(this._taskInfo) && this._taskInfo.isMediaAssessment()) {
            console.log("Reading time service: Not starting inactivity timeout service for media assessment")
            this.InactivityTimeoutService.stopWatch();
        } else {
            console.log("Reading time service: Starting ITS");
            this.InactivityTimeoutService.startWatch();
        }

        this.enableCKEditorInput();
    }

    /**
     * Handles the unlock keyboard signal by calling unlockKeyboard.
     */
    unlockKeyboardSignal() {
        this.unlockKeyboard();
    }

    /**
     * Handles the lock keyboard signal by calling lockKeyboard.
     */
    lockKeyboardSignal() {
        this.lockKeyboard();
    }

    /**
     * Starts the reading time countdown and sets up related event listeners.
     */
    startReadingTime() {
        this._readingTimeStarted = true;
        var _alreadyUsedTimeMS = 0;

        var TaskFlowService = this.$injector.get('TaskFlowService');
        if (TaskFlowService.hasExistingDataLastResult()) {
            _alreadyUsedTimeMS = this.$bridges.data().get('timeElapsed_readingTime');
            if (!angular.isDefined(_alreadyUsedTimeMS)) {
                _alreadyUsedTimeMS = 0;
            }

            if (this._readingTime - _alreadyUsedTimeMS <= 0) {
                this.startTask();
                return false;
            }
        }

        if (this._readingTime === 0) {
            this.startTask();
            return false;
        }

        this.lockKeyboard();

        if (this._taskCountdown.getTotalAvailableMillis() === 0) {
            this._taskCountdown.resetTime(this._readingTime, _alreadyUsedTimeMS, 'readingTime');

            var onTimeExpiredReadingTime = () => {
                this.onExpiredReadingTime();
            };
            this.$signals.on('countdownExpiredReadingTime', onTimeExpiredReadingTime);
        }

        this.$rootScope.$on('$routeChangeSuccess', (next, current) => {
            if (this._taskCountdown.getMode() !== 'readingTime') return false;
            if (this.$route.current.regexp.test("/invigilatorMenu/:taskUUID") ||
                this.$route.current.regexp.test("/invigilatorMenu/:taskUUID/:returnTo") ||
                this.$route.current.regexp.test("/pause/:taskUUID")) {
                this.unlockKeyboard();
                this.enableCKEditorInput();
            } else if (this.$route.current.regexp.test('/sheet/:sheetUUID') || this.$route.current.regexp.test('/overview/:taskUUID')) {
                this.lockKeyboard();
                this.disableCKEditorInput();
            }
        });

        this.$signals.on('readingTimeUnlockKeyboard', this.unlockKeyboardSignal.bind(this));
        this.$signals.on('readingTimeLockKeyboard', this.lockKeyboardSignal.bind(this));
    }

    /**
     * Handles the expired reading time event by opening a modal and setting flags.
     */
    onExpiredReadingTime() {
        if (!this._onExpiredReadingTimeModalOpened) {
            this._onExpiredReadingTimeModalOpened = true;

            var _expiredReadingTimeModalInstance = this.$uibModal.open({
                templateUrl: "/views/ib/modules/sheet/ExpiredReadingTimeModal.html",
                windowClass: "reading-time-modal",
                transclude: false,
                backdrop: 'static',
                keyboard: false,
                controller: ['$scope', '$uibModalInstance', 'TaskFlowService', 'taskCountdown', 'startTask', '$location',
                    function ($scope, $uibModalInstance, TaskFlowService, taskCountdown, startTask, $location) {
                        var url = $location.url();
                        startTask();
                        
                        $scope.onStartTask = function() {
                            startTask();
                            $uibModalInstance.close();
                        };

                        $scope.$on('$routeChangeStart', function() {
                            $uibModalInstance.close();
                        });
                    }
                ],
                resolve: {
                    taskCountdown: () => this._taskCountdown,
                    startTask: () => this.startTask.bind(this)
                }
            });

            _expiredReadingTimeModalInstance.result.then(() => {
                this._onExpiredReadingTimeModalOpened = false;
                this._taskCountdown.setMode('task');
            });

            return _expiredReadingTimeModalInstance;
        }
    }
}

export default ReadingTimeService;